/**
 * ConciergePinPicker
 * ──────────────────
 * Places the two map pins a hotel pickup needs: where the driver collects the
 * guest, and where they drop them.
 *
 * Why pins at all
 * ───────────────
 * The request used to carry only text. "Hotel lobby" is a perfectly good note
 * for a human and completely useless to a navigation app, so a driver either
 * knew the property already or phoned the guest. The pins also feed dispatch:
 * without a pickup coordinate, ranking drivers by who is nearest is impossible,
 * so offers went out on certification and reliability alone.
 *
 * One map, not two
 * ────────────────
 * A dialog with two stacked maps is unusable on a laptop. Instead there is a
 * single map and a toggle for which end you are placing, with both ends' status
 * always visible above it so an operator can never submit thinking they pinned
 * something they didn't.
 *
 * Pins are optional on purpose. A concierge on the phone in a hurry should be
 * able to log the job in fifteen seconds; an unpinned request still dispatches,
 * it just falls back to a text search for directions. The banner tells the
 * operator exactly what the driver will get either way.
 */

import { useState } from 'react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { FestivalVenuePinPicker } from '@/components/FestivalVenuePinPicker';
import { MapPin, Flag, Check, X, AlertTriangle } from 'lucide-react';

/** Emerald for pickup, amber for drop-off — matches the mobile route colours. */
const PICKUP_COLOR = '#10b981';
const DROPOFF_COLOR = '#f59e0b';

export interface ConciergePinPatch {
  pickupLat?: number | null;
  pickupLng?: number | null;
  dropoffLat?: number | null;
  dropoffLng?: number | null;
}

export interface ConciergePinPickerProps {
  pickupLabel: string;
  dropoffLabel: string;
  pickupLat: number | null;
  pickupLng: number | null;
  dropoffLat: number | null;
  dropoffLng: number | null;
  onChange: (patch: ConciergePinPatch) => void;
}

export function ConciergePinPicker({
  pickupLabel,
  dropoffLabel,
  pickupLat,
  pickupLng,
  dropoffLat,
  dropoffLng,
  onChange,
}: ConciergePinPickerProps) {
  const [end, setEnd] = useState<'pickup' | 'dropoff'>('pickup');

  const hasPickup = pickupLat != null && pickupLng != null;
  const hasDropoff = dropoffLat != null && dropoffLng != null;
  const isPickup = end === 'pickup';

  const activeLat = isPickup ? pickupLat : dropoffLat;
  const activeLng = isPickup ? pickupLng : dropoffLng;

  const handleChange = (lat: number, lng: number) => {
    onChange(
      isPickup
        ? { pickupLat: lat, pickupLng: lng }
        : { dropoffLat: lat, dropoffLng: lng },
    );
  };

  const clearActive = () => {
    onChange(
      isPickup
        ? { pickupLat: null, pickupLng: null }
        : { dropoffLat: null, dropoffLng: null },
    );
  };

  return (
    <div className="rounded-lg border border-gray-800 bg-gray-950/60 p-3 space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-gray-300 text-xs flex items-center gap-2">
          <MapPin className="w-3.5 h-3.5" /> Map pins (optional)
        </Label>
        {hasPickup || hasDropoff ? (
          <button
            type="button"
            onClick={clearActive}
            className="text-xs text-gray-500 hover:text-gray-300 flex items-center gap-1"
          >
            <X className="w-3 h-3" /> Clear {isPickup ? 'pickup' : 'drop-off'}
          </button>
        ) : null}
      </div>

      {/* Which end am I placing. Each button doubles as that end's status,
          so the operator never has to switch tabs to check. */}
      <div className="grid grid-cols-2 gap-2">
        <EndButton
          active={isPickup}
          done={hasPickup}
          color={PICKUP_COLOR}
          icon={<MapPin className="w-3.5 h-3.5" />}
          title="Pickup"
          subtitle={pickupLabel.trim() || 'no label yet'}
          onClick={() => setEnd('pickup')}
        />
        <EndButton
          active={!isPickup}
          done={hasDropoff}
          color={DROPOFF_COLOR}
          icon={<Flag className="w-3.5 h-3.5" />}
          title="Drop-off"
          subtitle={dropoffLabel.trim() || 'no label yet'}
          onClick={() => setEnd('dropoff')}
        />
      </div>

      <FestivalVenuePinPicker
        lat={activeLat}
        lng={activeLng}
        themeColor={isPickup ? PICKUP_COLOR : DROPOFF_COLOR}
        emoji={isPickup ? '🏨' : '🏁'}
        label={
          isPickup
            ? pickupLabel.trim() || 'Pickup'
            : dropoffLabel.trim() || 'Drop-off'
        }
        // No halo: a hotel door has no catchment radius, and drawing one
        // would imply the driver can stop anywhere in that circle.
        haloRadiusM={null}
        onChange={handleChange}
      />

      {!hasPickup ? (
        <p className="text-[11px] text-amber-400/90 flex items-start gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
          <span>
            Without a pickup pin the driver's Directions button just searches
            for the text above, and dispatch can't rank drivers by who is
            closest. Worth the ten seconds for a hotel you'll use again.
          </span>
        </p>
      ) : (
        <p className="text-[11px] text-emerald-400/90 flex items-center gap-1.5">
          <Check className="w-3.5 h-3.5 shrink-0" />
          Driver gets turn-by-turn to this exact spot
          {hasDropoff ? ' and to the drop-off.' : '.'}
        </p>
      )}
    </div>
  );
}

function EndButton({
  active,
  done,
  color,
  icon,
  title,
  subtitle,
  onClick,
}: {
  active: boolean;
  done: boolean;
  color: string;
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left rounded-md border px-2.5 py-2 transition-colors ${
        active
          ? 'border-gray-600 bg-gray-800/80'
          : 'border-gray-800 bg-gray-900/60 hover:border-gray-700'
      }`}
    >
      <span
        className="text-xs font-semibold flex items-center gap-1.5"
        style={{ color }}
      >
        {icon}
        {title}
        {done ? (
          <Check className="w-3 h-3 text-emerald-400" />
        ) : (
          <span className="text-[10px] font-normal text-gray-500">not pinned</span>
        )}
      </span>
      <span className="block text-[11px] text-gray-500 truncate mt-0.5">
        {subtitle}
      </span>
    </button>
  );
}

export default ConciergePinPicker;
